import { HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { EventTypes } from '@models/server-common';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { AuthenticatedUser } from '@quorum/authentication/state';
import { CompareObjectsPipe, DeepCopyPipe, MomentPipe } from '@quorum/common-pipes';
import { CommunicatorFacade } from '@quorum/communicator-ngrx';
import {
  ServiceAppointmentQueryParameters as AppointmentQueryParameters,
  ConversationQueryParameters,
  UserQueryParameters,
} from '@quorum/models/xs-query';
import {
  ServiceAppointment as Appointment,
  Conversation,
  DigitalCheckInStatuses,
  Employee,
  Event,
  Member,
  ServiceCode,
  TransportOption,
} from '@quorum/models/xs-resource';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { TwoActionDialogComponent, TwoActionDialogData } from '@quorum/sha-components-ng-ui';
import { RouterStateService } from '@quorum/sha-router';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import { GroupByPipe } from 'ngx-pipes';
import { Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
// eslint-disable-next-line max-len

@Component({
  selector: 'svc-todays-appointments',
  templateUrl: './todays-appointments.component.html',
  providers: [GroupByPipe],
  styleUrls: ['./todays-appointments.component.scss'],
})
export class TodaysAppointmentsComponent implements OnInit, OnDestroy {
  myAppointments$: Observable<any>;
  myUnacknowledgedAppointments$: Observable<any>;
  authenticatedEmployee$: Observable<Employee>;
  authenticatedUser$: Observable<AuthenticatedUser>;
  deleteTodaysAppointmentSub: Subscription;
  techPromptForm: FormGroup = this.formBuilder.group({
    workOrderId: [null],
  });
  workOrderFound = true;
  unacknowledgedAppointmentsCollapse = false;
  unacknowledgedAppointmentCount = 0;
  groupedAppointments: Array<any>;
  groupedUnacknowledgedAppointments: Array<any>;
  today: Date;
  tomorrow: Date;
  yesterday: Date;
  dateTo: Date;
  dateFrom: Date;
  noResultsDateDisplay = 'for today';
  noResultsDisplayIcon = 'calendar_empty';
  isPopoverOpen = false;
  digitalCheckInEnabled$: Observable<boolean>;
  dateFilterOpenTab: number;
  appointmentViewOpenTab = 0;

  todayFilter = {
    appointmentDateFrom: new Date(),
    appointmentDateTo: new Date(),
  };

  tomorrowFilter = {
    appointmentDateFrom: new Date().setDate(new Date().getDate() + 1),
    appointmentDateTo: new Date().setDate(new Date().getDate() + 1),
  };

  showRidePreferences = false;
  showServiceCodes = false;
  selectedRidePreference = 'Ride Preference';
  selectedServiceCode = 'Service Code';

  constructor(
    public serviceLaneFacade: ServiceLaneFacade,
    private router: Router,
    private authenticationStateService: AuthenticationStateService,
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    public dialog: MatDialog,
    private groupByPipe: GroupByPipe,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    private routerStateService: RouterStateService,
    private systemControlStateService: SystemControlStateService,
    private communicatorFacade: CommunicatorFacade
  ) {}

  ngOnInit() {
    this.serviceLaneFacade.appointmentFilters$.pipe(take(1)).subscribe((appointmentFilters) => {
      if (!this.serviceLaneFacade.isToday(appointmentFilters.appointmentDateFrom)) {
        this.dateFilterOpenTab = 2;
      } else {
        this.dateFilterOpenTab = 0;
      }
      this.dateFrom = appointmentFilters.appointmentDateFrom;
      this.dateTo = appointmentFilters.appointmentDateTo;
    });
    this.getNoResultsIcon(null, null);
    this.myAppointments$ = this.serviceLaneFacade.myAppointmentsToday$.pipe(
      tap((appointments: any[]) => {
        this.groupedUnacknowledgedAppointments = appointments.filter(
          (myAppointment: any) => myAppointment.appointment?.digitalCheckInStatus === 'CHECKED_IN'
        );
        this.unacknowledgedAppointmentCount = this.groupedUnacknowledgedAppointments?.length;
        this.groupedAppointments = this.groupByPipe.transform(appointments, 'date');
        this.groupedUnacknowledgedAppointments = this.groupByPipe.transform(
          this.groupedUnacknowledgedAppointments,
          'date'
        );
        this.today = new Date();
        this.tomorrow = new Date(this.today);
        this.tomorrow.setDate(this.tomorrow.getDate() + 1);
        this.yesterday = new Date(this.today);
        this.yesterday.setDate(this.yesterday.getDate() - 1);
      })
    );

    this.digitalCheckInEnabled$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      filter((emp) => emp != null),
      take(1),
      switchMap((employee) => {
        return this.systemControlStateService
          .getSystemControlValues(['CL_DIGITAL_CHECKIN_ENABLED'], employee.rooftopId)
          .pipe(
            take(1),
            map((scts) => {
              return scts['CL_DIGITAL_CHECKIN_ENABLED'].value == '1' ? true : false;
            })
          );
      })
    );

    this.deleteTodaysAppointmentSub = this.serviceLaneFacade.deleteTodaysAppointment$.subscribe();
    this.authenticatedEmployee$ = this.authenticationStateService.selectAuthenticatedEmployee();
    this.authenticatedUser$ = this.authenticationStateService.selectAuthenticatedUser();
  }

  ngOnDestroy() {
    this.deleteTodaysAppointmentSub.unsubscribe();
  }

  // openAppointment(appointment: any) {
  //   if (appointment.vehicle) {
  //     this.router.navigate([`vehicle`, `${appointment.vehicle.id}`, `appointments`, `${appointment.id}`]);
  //   } else {
  //     this.router.navigate([`freeform`, `appointments`, `${appointment.id}`]);
  //   }
  // }

  // deleteTodaysAppointment(appointment: Appointment, event: any) {
  //   event.preventDefault();
  //   event.stopImmediatePropagation();
  //   const dialogRef = this.dialog.open(AppointmentConfirmDeleteDialogComponent, {
  //     width: '400px',
  //     data: {},
  //   });
  //   dialogRef.afterClosed().subscribe((result: string) => {
  //     if (result == 'yes') {
  //       this.serviceLaneFacade.deleteTodaysAppointmentFromServer(appointment);
  //     }
  //   });
  // }

  trackByFunction(index: number, item: Appointment) {
    return item.id;
  }

  // goToInspection(value: any, authenticatedEmployee: Employee, authenticatedUser: AuthenticatedUser) {
  //   this.apiService.get<WorkOrder>(`v/1/fixed-operations/work-orders/` + value.workOrderId).subscribe((workOrder) => {
  //     if (workOrder) {
  //       this.workOrderFound = true;
  //       const params: SystemPathwayQueryParameters = new SystemPathwayQueryParameters();
  //       params.rooftopId = authenticatedEmployee.rooftopId.toString();
  //       params.descriptionCode = 'web_root_url';
  //       this.apiService.get(`/v/1/system/system-pathways`, { params: params }).subscribe((pathways) => {
  //         const pathway: SystemPathway = pathways[0];

  //         this.apiService.legacyAuthGet(`general/store/${authenticatedUser.user.destination}`).subscribe((f) => {
  //           window.open(
  //             `${pathway.pathway.endsWith('/') ? pathway.pathway : pathway.pathway + '/'}vip/workorder/${
  //               value.workOrderId
  //             }?token=${authenticatedUser.token}&authUrl=${f.AuthenticationUrl}&apikey=${f.ApiKey}&version=${f.Version}`
  //           );
  //         });
  //       });
  //     } else {
  //       this.workOrderFound = false;
  //     }
  //   });
  // }

  applyAppointmentFilters(appointmentFilters: any, authenticatedEmployee: Employee) {
    this.dateFrom = new Date(appointmentFilters.appointmentDateFrom);
    this.dateTo = new Date(appointmentFilters.appointmentDateTo);
    const today = new Date();
    this.dateFrom.setHours(0, 0, 0);
    this.dateTo.setHours(23, 59, 59);
    today.setHours(12, 0, 0);

    let appointmentQueryParams = new AppointmentQueryParameters({
      appointmentDateFrom: this.dateFrom.toISOString(),
      appointmentDateTo: this.dateTo.toISOString(),
      statusId: '1,2',
      department: 'M',
      rooftopId: authenticatedEmployee.rooftopId.toString(),
      embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption,customerAssociate,vehicle',
    });
    if (appointmentFilters.myAppointmentsToggle) {
      appointmentQueryParams = { ...appointmentQueryParams, serviceAdvisorId: authenticatedEmployee.associateId };
    }
    this.router.routerState.root.queryParams.pipe(take(1)).subscribe((queryParams) => {
      if (
        new CompareObjectsPipe().transform(appointmentQueryParams, queryParams) === false ||
        (queryParams.serviceAdvisorId && !appointmentQueryParams.serviceAdvisorId)
      ) {
        appointmentQueryParams = {
          ...appointmentQueryParams,
          serviceCode: queryParams?.serviceCode,
          transportOptionId: queryParams?.transportOptionId?.toString(),
        };
        this.serviceLaneFacade.clearState();
        this.updateRouter(appointmentQueryParams);

        if (this.dateFrom.getMonth() == this.dateTo.getMonth() && this.dateFrom.getDate() == this.dateTo.getDate()) {
          if (this.dateTo.getDate() == today.getDate()) {
            this.noResultsDateDisplay = 'for today';
          } else {
            this.noResultsDateDisplay = `${new MomentPipe().transform(this.dateTo, 'MMMM Do')}`;
          }
        } else {
          this.noResultsDateDisplay = `${new MomentPipe().transform(
            this.dateFrom,
            'MMMM Do'
          )} to ${new MomentPipe().transform(this.dateTo, 'MMMM Do')}`;
        }
        this.getNoResultsIcon(this.dateFrom, this.dateTo);
      }
    });
  }

  applyServiceCodeFilter(serviceCode: ServiceCode, authenticatedEmployee: Employee) {
    let appointmentQueryParams: AppointmentQueryParameters;
    this.selectedServiceCode = serviceCode ? serviceCode.description : 'Service Code';
    this.dateFrom = new Date(this.dateFrom);
    this.dateTo = new Date(this.dateTo);
    const today = new Date();
    this.dateFrom.setHours(0, 0, 0);
    this.dateTo.setHours(23, 59, 59);
    today.setHours(12, 0, 0);

    this.router.routerState.root.queryParams.pipe(take(1)).subscribe((queryParams) => {
      if (Object.keys(queryParams).length === 0) {
        appointmentQueryParams = new AppointmentQueryParameters({
          appointmentDateFrom: this.dateFrom.toISOString(),
          appointmentDateTo: this.dateTo.toISOString(),
          statusId: '1,2',
          department: 'M',
          rooftopId: authenticatedEmployee.rooftopId.toString(),
          embed:
            'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption,customerAssociate,vehicle',
        });
      } else {
        appointmentQueryParams = new AppointmentQueryParameters({
          ...queryParams,
        });
      }

      if (serviceCode) {
        appointmentQueryParams = { ...appointmentQueryParams, serviceCode: serviceCode.serviceCode };
      } else {
        appointmentQueryParams = { ...appointmentQueryParams, serviceCode: null };
      }

      if (
        new CompareObjectsPipe().transform(appointmentQueryParams, queryParams) === false ||
        (queryParams.serviceAdvisorId && !appointmentQueryParams.serviceAdvisorId)
      ) {
        this.serviceLaneFacade.clearState();
        this.updateRouter(appointmentQueryParams);

        if (this.dateFrom.getMonth() == this.dateTo.getMonth() && this.dateFrom.getDate() == this.dateTo.getDate()) {
          if (this.dateTo.getDate() == today.getDate()) {
            this.noResultsDateDisplay = 'for today';
          } else {
            this.noResultsDateDisplay = `${new MomentPipe().transform(this.dateTo, 'MMMM Do')}`;
          }
        } else {
          this.noResultsDateDisplay = `${new MomentPipe().transform(
            this.dateFrom,
            'MMMM Do'
          )} to ${new MomentPipe().transform(this.dateTo, 'MMMM Do')}`;
        }
        this.getNoResultsIcon(this.dateFrom, this.dateTo);
      }
    });
  }

  applyRidePreferenceFilter(ridePreference: TransportOption, authenticatedEmployee: Employee) {
    let appointmentQueryParams: AppointmentQueryParameters;
    this.selectedRidePreference = ridePreference ? ridePreference.name : 'Ride Preference';
    this.dateFrom = new Date(this.dateFrom);
    this.dateTo = new Date(this.dateTo);
    const today = new Date();
    this.dateFrom.setHours(0, 0, 0);
    this.dateTo.setHours(23, 59, 59);
    today.setHours(12, 0, 0);

    this.router.routerState.root.queryParams.pipe(take(1)).subscribe((queryParams) => {
      if (Object.keys(queryParams).length === 0) {
        appointmentQueryParams = new AppointmentQueryParameters({
          appointmentDateFrom: this.dateFrom.toISOString(),
          appointmentDateTo: this.dateTo.toISOString(),
          statusId: '1,2',
          department: 'M',
          rooftopId: authenticatedEmployee.rooftopId.toString(),
          embed:
            'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption,customerAssociate,vehicle',
        });
      } else {
        appointmentQueryParams = new AppointmentQueryParameters({
          ...queryParams,
        });
      }

      if (ridePreference) {
        appointmentQueryParams = { ...appointmentQueryParams, transportOptionId: ridePreference.id.toString() };
      } else {
        appointmentQueryParams = { ...appointmentQueryParams, transportOptionId: null };
      }
      if (
        new CompareObjectsPipe().transform(appointmentQueryParams, queryParams) === false ||
        (queryParams.serviceAdvisorId && !appointmentQueryParams.serviceAdvisorId)
      ) {
        this.serviceLaneFacade.clearState();
        this.updateRouter(appointmentQueryParams);

        if (this.dateFrom.getMonth() == this.dateTo.getMonth() && this.dateFrom.getDate() == this.dateTo.getDate()) {
          if (this.dateTo.getDate() == today.getDate()) {
            this.noResultsDateDisplay = 'for today';
          } else {
            this.noResultsDateDisplay = `${new MomentPipe().transform(this.dateTo, 'MMMM Do')}`;
          }
        } else {
          this.noResultsDateDisplay = `${new MomentPipe().transform(
            this.dateFrom,
            'MMMM Do'
          )} to ${new MomentPipe().transform(this.dateTo, 'MMMM Do')}`;
        }
        this.getNoResultsIcon(this.dateFrom, this.dateTo);
      }
    });
  }

  getNoResultsIcon(dateFrom: Date, dateTo: Date) {
    if (this.noResultsDateDisplay == 'for today') {
      this.noResultsDisplayIcon = `calendar_day_${new Date().getDate()}`;
    } else if (dateFrom.getDate() == dateTo.getDate()) {
      this.noResultsDisplayIcon = `calendar_day_${dateTo.getDate()}`;
    } else {
      this.noResultsDisplayIcon = 'calendar_empty';
    }
  }

  updateRouter(queryParams: any) {
    const navExtras: NavigationExtras = {
      queryParams: queryParams,
    };
    this.routerStateService.go('', navExtras);
  }

  // getCheckedInCount(appointments: Appointment[]) {
  //   console.log(
  //     'CI',
  //     appointments.filter((apt) => {
  //       return apt.digitalCheckInStatus == 'CHECKED_IN';
  //     }).length
  //   );
  //   return appointments.filter((apt) => {
  //     return apt.digitalCheckInStatus == 'CHECKED_IN';
  //   }).length;
  // }

  navigateToAppointment(item: any, digitalCheckInEnabled: boolean, authenticatedUser: AuthenticatedUser) {
    if (!digitalCheckInEnabled) {
      if (!item.vehicle) {
        this.router.navigate([`freeform-appointment/${item.id}`]);
      } else {
        this.router.navigate([`/vehicle/${item.vehicle.id}/appointments/${item.id}`]);
      }
    } else {
      if (item.digitalCheckInStatus == DigitalCheckInStatuses.CheckedIn) {
        const dialogData: TwoActionDialogData = {
          actionTwoCaption: 'Yes',
          actionOneCaption: 'No',
          content:
            'Would you like acknowledge the customers check in and send them an automated Communicator message? If you do not one will be automatically sent upon work order creation.',
          title: 'Acknowledge Check In?',
        };

        const dialogRef = this.dialog.open(TwoActionDialogComponent, {
          width: '400px',
          data: dialogData,
        });

        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            //send communicator message
            const conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
              transactionTypeId: 6,
              messageTypeId: 31,
              transactionId: item.id,
            });

            const event: Event = new Event({
              conversationId: null,
              memberId: authenticatedUser.user.id.toString(),
              eventTypeId: EventTypes.Message,
              content: `Hello ${item?.customer?.firstName}, this is an automated message to inform you your Service Advisor has completed check in. Your vehicle is currently awaiting an inspection. You can get up to date status updates by revisiting your check-in link.`,
              guid: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
              sentDate: new Date(),
              sent: false,
              sendToCustomer: true,
              id: Math.floor(Math.random() * (9999999 - 999999)) + 999999,
            });

            const conversation: Conversation = new Conversation({
              dmsMessageTypeId: 31,
              dmsTransactionTypeId: 6,
              dmsTransactionId: item.id,
              createdBy: authenticatedUser.user.id,
            });

            const members: Member[] = [];
            members.push(
              new Member({
                userId: authenticatedUser.user.id.toString(),
                avatarUrl: authenticatedUser.user.avatarUrl,
                firstName: authenticatedUser.user.firstName,
                lastName: authenticatedUser.user.lastName,
                nickname: authenticatedUser.user.firstName,
                isEmployee: true,
                isRead: true,
                isArchived: false,
                isExcluded: false,
                isPinnedToUi: false,
              })
            );

            members.push(
              new Member({
                userId: item.customer.id,
                firstName: item.customer.firstName,
                lastName: item.customer.lastName,
                nickname: item.customer.firstName,
                isEmployee: false,
                isRead: true,
                isArchived: false,
                isExcluded: false,
                isPinnedToUi: false,
              })
            );

            const userParams: UserQueryParameters = new UserQueryParameters({
              username: `Communicator${authenticatedUser.user.destination}`,
            });

            this.apiService
              .get<any>('quorum/users', { params: userParams })
              .pipe(
                filter((users) => users),
                map((users) => {
                  const user = users[0];

                  const member: Member = new Member();
                  member.userId = user.id.toString();
                  member.avatarUrl = user.avatarUrl;
                  member.firstName = user.firstName;
                  member.lastName = user.lastName;
                  member.nickname = user.firstName;
                  member.isEmployee = true;
                  member.isRead = true;
                  member.isArchived = false;
                  member.isExcluded = false;
                  member.isPinnedToUi = false;
                  members.push(member);
                })
              )
              .subscribe(() => {
                const optionalHeader = new HttpHeaders({ 'Use-Default-Template': 'true' });
                this.communicatorFacade.sendCommunicatorMessage(
                  conversationQueryParameters,
                  conversation,
                  members,
                  event,
                  optionalHeader
                );
                this.serviceLaneFacade.selectTodaysAppointmentById(item.id).subscribe((appointment) => {
                  const updatedAppointment: Appointment = new DeepCopyPipe().transform(appointment);
                  updatedAppointment.digitalCheckInStatus = DigitalCheckInStatuses.Awknowledged;

                  this.serviceLaneFacade.updateAppointmentCheckInStatus$.pipe(take(1)).subscribe((apt) => {
                    this.router.navigate([`/vehicle/${item.vehicle.id}/appointments/${item.id}`]);
                  });
                  this.serviceLaneFacade.updateAppointmentOnServer(updatedAppointment, {
                    embed:
                      'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption,customerAssociate,vehicle',
                  });
                });
              });
          } else {
            this.router.navigate([`/vehicle/${item.vehicle.id}/appointments/${item.id}`]);
          }
        });
      } else {
        if (!item.vehicle) {
          this.router.navigate([`freeform-appointment/${item.id}`]);
        } else {
          this.router.navigate([`/vehicle/${item.vehicle.id}/appointments/${item.id}`]);
        }
      }
    }
  }

  dateFilterToggleTabs(tabNumber: number) {
    this.dateFilterOpenTab = tabNumber;
  }

  appointmentViewToggleTabs(tabNumber: number) {
    this.appointmentViewOpenTab = tabNumber;
  }

  toggleRidePreferenceDropdown() {
    this.showRidePreferences = !this.showRidePreferences;
  }

  toggleServiceCodeDropdown() {
    this.showServiceCodes = !this.showServiceCodes;
  }

  clickedOutsideRidePreference() {
    this.showRidePreferences = false;
  }

  clickedOutsideServiceCode() {
    this.showServiceCodes = false;
  }

  resetFilters(authenticatedEmployee: Employee) {
    let appointmentQueryParams: AppointmentQueryParameters;
    this.selectedServiceCode = 'Service Code';
    this.selectedRidePreference = 'Ride Preference';
    this.dateFilterToggleTabs(0);

    this.dateFrom = new Date(this.todayFilter.appointmentDateFrom);
    this.dateTo = new Date(this.todayFilter.appointmentDateTo);
    const today = new Date();
    this.dateFrom.setHours(0, 0, 0);
    this.dateTo.setHours(23, 59, 59);
    today.setHours(12, 0, 0);

    this.router.routerState.root.queryParams.pipe(take(1)).subscribe((queryParams) => {
      appointmentQueryParams = new AppointmentQueryParameters({
        appointmentDateFrom: this.dateFrom.toISOString(),
        appointmentDateTo: this.dateTo.toISOString(),
        statusId: '1,2',
        department: 'M',
        rooftopId: authenticatedEmployee.rooftopId.toString(),
        embed:
          'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption,customerAssociate,vehicle',
      });

      this.serviceLaneFacade.clearState();
      this.noResultsDateDisplay = 'for today';
      this.getNoResultsIcon(this.dateFrom, this.dateTo);

      if (
        new CompareObjectsPipe().transform(appointmentQueryParams, queryParams) === false ||
        (queryParams.serviceAdvisorId && !appointmentQueryParams.serviceAdvisorId)
      ) {
        this.updateRouter(appointmentQueryParams);
      } else {
        this.serviceLaneFacade.getAppointments(appointmentQueryParams, authenticatedEmployee);
      }
    });
  }

  // toggleTabs($tabNumber: number) {
  //   this.dateFilterOpenTab = $tabNumber;
  // }
}
